<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { FairIcon } from '@/ui/primitives/FairIcon'
import {
  FAIR_VARIANT_COMPARE_MIN,
  getFairVariantCompareTrayDialOffset,
  type FairVariantCompareItem,
  type FairVariantCompareTrayLayout,
} from '@/ui/tokens/fair-variant-compare.data'
import { COMPARISON_ROUTE } from '@/router/routes'

interface FairVariantCompareTrayProps {
  items: FairVariantCompareItem[]
  /** Hide on compare page itself */
  visible?: boolean
  /** Speed-dial expansion layout */
  layout?: FairVariantCompareTrayLayout
}

const props = withDefaults(defineProps<FairVariantCompareTrayProps>(), {
  visible: true,
  layout: 'semi-circle',
})

const emit = defineEmits<{
  clear: []
  remove: [variantId: number]
}>()

const { t } = useI18n()
const router = useRouter()

const expanded = ref(false)
const rootRef = ref<HTMLElement | null>(null)

type DialAction =
  | { kind: 'thumb'; item: FairVariantCompareItem; key: string }
  | { kind: 'compare'; key: 'compare' }
  | { kind: 'clear'; key: 'clear' }

const show = computed(
  () => props.visible && props.items.length > 0,
)

const canGo = computed(() => props.items.length >= FAIR_VARIANT_COMPARE_MIN)

/** Closest to FAB first: thumbs → Compare → Clear (furthest). */
const dialActions = computed((): DialAction[] => {
  const thumbs: DialAction[] = props.items.map((item) => ({
    kind: 'thumb',
    item,
    key: `thumb-${item.variantId}`,
  }))
  return [
    ...thumbs,
    { kind: 'compare', key: 'compare' },
    { kind: 'clear', key: 'clear' },
  ]
})

const dialCount = computed(() => dialActions.value.length)

watch(
  () => props.items.length,
  (count) => {
    if (count === 0) {
      expanded.value = false
    }
  },
)

function dialStyle(index: number): Record<string, string> {
  const offset = expanded.value
    ? getFairVariantCompareTrayDialOffset(
        index,
        dialCount.value,
        props.layout,
      )
    : { x: 0, y: 0 }

  return {
    '--fair-variant-compare-tray-x': `${offset.x}px`,
    '--fair-variant-compare-tray-y': `${offset.y}px`,
    '--fair-variant-compare-tray-delay': expanded.value
      ? `${index * 35}ms`
      : `${(dialCount.value - 1 - index) * 25}ms`,
  }
}

function toggleExpanded() {
  expanded.value = !expanded.value
}

function collapse() {
  expanded.value = false
}

function handleCompare() {
  if (!canGo.value) {
    return
  }
  collapse()
  void router.push(COMPARISON_ROUTE)
}

function handleClear() {
  emit('clear')
  collapse()
}

function handleRemove(variantId: number) {
  emit('remove', variantId)
}

function onDocumentPointerDown(event: PointerEvent) {
  if (!expanded.value || !rootRef.value) {
    return
  }
  const target = event.target
  if (!(target instanceof Node)) {
    return
  }
  if (!rootRef.value.contains(target)) {
    collapse()
  }
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && expanded.value) {
    collapse()
  }
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocumentPointerDown, true)
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      ref="rootRef"
      class="fair-variant-compare-tray"
      :class="[
        `fair-variant-compare-tray--${layout}`,
        { 'fair-variant-compare-tray--expanded': expanded },
      ]"
      role="complementary"
      :aria-label="t('fair_compare.tray_aria')"
    >
      <ul
        class="fair-variant-compare-tray__actions"
        role="menu"
        :aria-hidden="!expanded"
      >
        <li
          v-for="(action, index) in dialActions"
          :key="action.key"
          class="fair-variant-compare-tray__action"
          :class="`fair-variant-compare-tray__action--${action.kind}`"
          :style="dialStyle(index)"
          role="none"
        >
          <button
            v-if="action.kind === 'thumb'"
            type="button"
            class="fair-variant-compare-tray__thumb"
            role="menuitem"
            :aria-label="t('fair_compare.remove')"
            :tabindex="expanded ? 0 : -1"
            @click="handleRemove(action.item.variantId)"
          >
            <img
              v-if="action.item.imageUrl"
              :src="action.item.imageUrl"
              :alt="action.item.title"
              loading="lazy"
              decoding="async"
            >
            <span
              v-else
              class="fair-variant-compare-tray__thumb-fallback"
            >
              {{ action.item.brand.slice(0, 1) }}
            </span>
            <span class="fair-variant-compare-tray__thumb-remove">
              <FairIcon
                name="close"
                :size="12"
              />
            </span>
          </button>

          <button
            v-else-if="action.kind === 'compare'"
            type="button"
            class="fair-variant-compare-tray__action-btn fair-variant-compare-tray__action-btn--compare"
            role="menuitem"
            :disabled="!canGo"
            :aria-label="t('fair_compare.compare_cta')"
            :tabindex="expanded ? 0 : -1"
            @click="handleCompare"
          >
            <FairIcon
              name="compare"
              :size="18"
            />
          </button>

          <button
            v-else
            type="button"
            class="fair-variant-compare-tray__action-btn fair-variant-compare-tray__action-btn--clear"
            role="menuitem"
            :aria-label="t('fair_compare.clear')"
            :tabindex="expanded ? 0 : -1"
            @click="handleClear"
          >
            <FairIcon
              name="close"
              :size="14"
            />
          </button>
        </li>
      </ul>

      <button
        type="button"
        class="fair-variant-compare-tray__fab"
        :aria-expanded="expanded"
        :aria-haspopup="true"
        :aria-label="
          expanded
            ? `${t('fair_compare.tray_collapse')} (${items.length})`
            : `${t('fair_compare.tray_expand')} (${items.length})`
        "
        @click="toggleExpanded"
      >
        <FairIcon
          name="compare"
          :size="20"
        />
        <span
          class="fair-variant-compare-tray__badge"
          aria-hidden="true"
        >
          {{ items.length }}
        </span>
      </button>
    </div>
  </Teleport>
</template>
